import { formatMoney, t } from "@hakili/i18n";
import { Button, Callout, Table } from "@hakili/ui";
import { Info, RefreshCw } from "lucide-react";
import { useState } from "react";

import { nextStepOf } from "../stage-words.js";
import { FileWho, ListCard, ListState, PillTabs, StageDotChip, Veilleur } from "../file-list.js";
import { DASH, type FichierBanque, fill, GradeChip, initialsOf, productOf } from "./dossiers.js";
import { SkeletonRows } from "./skeleton-rows.js";

/**
 * WF-BNK-02 to D2's frames 01 to 04 and 66 (`DESIGN SPRINT`, D-1026-2): the title « Pipeline », the sentence under it DERIVED from the
 * rows (D2's « ce matin » needs a clock no screen holds, so no day is named), « le veilleur »'s line, seven pill tabs with their
 * counts, and the table as D2 draws it: the dark initials tile, the client, `CR-… · <véhicule>`, the amount, the note, the stage chip,
 * the next gesture. The chef's page is « Pipeline de l'équipe »: the same table with the analyst in the last column.
 *
 * NOT drawn, for want of a field (rule 3): D2's « Reçu » column (« ce matin », « il y a 6 j ») needs the door's day on `/bank/pipeline`
 * and `/bank/files`, and « aucun constat bloquant » in the veilleur's line needs a per-file count of blocking findings no door sends.
 *
 * The rows are the pipeline's own answer (`GET /bank/pipeline`: the caller's files) joined to the pool a file waits in before anyone
 * takes it (`GET /bank/files`), because a file that has just arrived belongs to nobody and the « Tous » view must show it.
 */
const TERMINAL = new Set(["decaisse", "refuse", "perdu"]);

/** D2's order of the open list: the new file first, « Chez Hakili » before the signature and its conditions (its own sequence, not the stage's). */
const LIST_ORDER = ["nouveau", "revue", "validation", "offre", "comite", "retour", "signature", "conditions"];
const rank = (stage: string): number => {
  const at = LIST_ORDER.indexOf(stage);
  return at === -1 ? LIST_ORDER.length : at;
};

const TABS: readonly {
  readonly id: string;
  readonly key: Parameters<typeof t>[0];
  readonly stages: readonly string[] | null;
}[] = [
  { id: "tous", key: "bank.pipeline.ongletTous", stages: null },
  { id: "traiter", key: "bank.pipeline.ongletATraiter", stages: ["nouveau", "revue"] },
  { id: "revue", key: "bank.pipeline.ongletRevue", stages: ["revue"] },
  { id: "offre", key: "bank.pipeline.ongletOffres", stages: ["validation", "offre"] },
  { id: "comite", key: "bank.pipeline.ongletComite", stages: ["comite"] },
  { id: "finalisation", key: "bank.pipeline.ongletFinalisation", stages: ["signature", "conditions"] },
  { id: "retour", key: "bank.pipeline.ongletHakili", stages: ["retour"] },
];

/** D2's « Reçu » words from the door's own count of days (`LISTS-DATA`): no clock here, and no time of day claimed. */
const receivedWords = (days: number | null): string =>
  days === null
    ? DASH
    : days === 0
      ? t("bank.pipeline.recuAujourdhui")
      : days === 1
        ? t("bank.pipeline.recuHier")
        : fill("bank.pipeline.recuJours", { n: String(days) });

/** The tag `lending.FileReassigned`'s consumer writes on the receiving analyst's notice (`notifications`' `dossier-confie`). */
export const ENTRUSTED_KIND = "dossier-confie";

/** One notice « Le dossier de X (REF) vous est confié. », the words the writer stored (frame 125, the founder's E2). */
export interface EntrustedNotice {
  readonly id: string;
  readonly fileId: string;
  readonly title: string;
  readonly line: string;
}

export function Pipeline(props: {
  readonly persona: "analyste" | "chef";
  readonly fichiers: readonly FichierBanque[];
  /** The lists' read: D2's skeleton (frame 03) while it runs, its error card with « Réessayer » (frame 04) when it failed. */
  readonly etat: "chargement" | "erreur" | "pret";
  readonly onRetry: () => void;
  readonly onOuvrir: (file: FichierBanque) => void;
  /** The chef's last column: the analyst's name by id, from the team's read. */
  readonly analystNames?: ReadonlyMap<string, string>;
  /** `P1-BKW-029` (frame 143): the caller's own id; a file held by it reads « Vous » / « chef du crédit » in the last column. */
  readonly selfId?: string;
  /**
   * Frame 125 (`EQUIPE-GESTURES` R4, the founder's E2): the files a chef has given her and she has not yet opened, each as an information
   * callout under the sentence. It goes when the file leaves her (it is no longer among `fichiers`) or when she opens it
   * (her act marks the row read, never a render).
   */
  readonly entrusted?: readonly EntrustedNotice[];
  readonly onReadEntrusted?: (id: string) => void;
  /** `L-5`, `M2`: the chef's pipeline filtered on one analyst (« Voir ses dossiers (N) »), by the row's own carrier. */
  readonly analystFilter?: {
    readonly userId: string;
    readonly fullName: string;
    readonly onClear: () => void;
  } | null;
}): React.JSX.Element {
  const [tab, setTab] = useState("tous");
  const chef = props.persona === "chef";
  const filter = props.analystFilter ?? null;
  // `L-5`, `M2`: the chef's pipeline on one analyst's files, the row's own carrier (WF-BNK-24's filter).
  const open = props.fichiers
    .filter((file) => !TERMINAL.has(file.stage) && (filter === null || file.analystUserId === filter.userId))
    // The analyst's list reads from the new file down (D2's order); the chef's keeps the door's own order (D2 groups it by analyst).
    .sort((one, other) => (chef ? 0 : rank(one.stage) - rank(other.stage)));
  const count = (stages: readonly string[]): number =>
    open.filter((file) => stages.includes(file.stage)).length;
  const received = count(["nouveau"]);
  const waiting = count(["offre"]);
  // `LISTS-DATA`: « aucun constat bloquant » is said only when the door counted 0 on EVERY new file; one finding, or one file with no register (`null`), and it is not.
  const noneBlocking =
    received > 0 &&
    open.filter((file) => file.stage === "nouveau").every((file) => file.blockingFindings === 0);
  const active = TABS.find((one) => one.id === tab) ?? TABS[0];
  const stages = chef ? null : (active?.stages ?? null);
  const visible = stages === null ? open : open.filter((file) => stages.includes(file.stage));
  const tabs = chef
    ? [{ id: "tous", label: t("bank.pipeline.ongletEnCours"), count: open.length }]
    : TABS.map((one) => ({
        id: one.id,
        label: t(one.key),
        count: one.stages === null ? open.length : count(one.stages),
      }));
  // While the lists are unread (or could not be) no count is told: a zero would be a figure nobody read.
  const ready = props.etat === "pret";
  const empty = ready && open.length === 0;
  return (
    <div data-testid="bank-pipeline">
      <h1
        className="mb-1 mt-2 text-[length:var(--hk-fs-h1)] font-semibold leading-tight tracking-[-0.01em]"
        data-testid="bank-pipeline-bonjour"
      >
        {chef ? t("bank.pipeline.titreChef") : t("bank.pipeline.titreAnalyste")}
      </h1>
      <p
        className="mb-[18px] max-w-[80ch] text-[length:var(--hk-fs-read)] text-[color:var(--hk-text-2)]"
        data-testid="bank-pipeline-ligne"
      >
        {chef ? (
          t("bank.pipeline.sousChef")
        ) : !ready ? null : (
          <>
            {received === 0 ? (
              t("bank.pipeline.aucunNouveau")
            ) : (
              <>
                <b className="text-[color:var(--hk-text)]">
                  {fill(received === 1 ? "bank.pipeline.nouveauUn" : "bank.pipeline.nouveauxN", {
                    n: String(received),
                  })}
                </b>
                {t(received === 1 ? "bank.pipeline.luUn" : "bank.pipeline.lusN")}
              </>
            )}
            {waiting === 0
              ? ""
              : ` ${waiting === 1 ? t("bank.pipeline.offreUne") : fill("bank.pipeline.offresN", { n: String(waiting) })}`}
          </>
        )}
      </p>
      {filter === null ? null : (
        <Callout
          tone="info"
          className="mb-4"
          testId="bank-pipeline-filtre-analyste"
          icon={<Info className="h-5 w-5" aria-hidden="true" />}
        >
          <p>
            {t("bank.pipeline.filtreDe")} <b>{filter.fullName}</b> {t("bank.pipeline.filtreSeulement")}{" "}
            <Button
              variant="ghost"
              size="sm"
              data-testid="bank-pipeline-filtre-effacer"
              onClick={filter.onClear}
            >
              {t("bank.pipeline.filtreToute")}
            </Button>
          </p>
        </Callout>
      )}
      {(chef ? [] : (props.entrusted ?? [])).map((notice) => {
        const file = props.fichiers.find((one) => one.id === notice.fileId);
        if (file === undefined) return null;
        return (
          <Callout
            key={notice.id}
            tone="info"
            className="mb-4"
            testId={`bank-pipeline-confie-${file.reference}`}
            icon={<Info className="h-5 w-5" aria-hidden="true" />}
          >
            <p className="font-semibold">{notice.title}</p>
            <p className="text-[length:var(--hk-fs-body)]">
              {notice.line}{" "}
              <Button
                variant="ghost"
                size="sm"
                data-testid={`bank-pipeline-confie-ouvrir-${file.reference}`}
                onClick={() => {
                  props.onReadEntrusted?.(notice.id);
                  props.onOuvrir(file);
                }}
              >
                {t("bank.pipeline.ouvrirDossier")}
              </Button>
            </p>
          </Callout>
        );
      })}
      {!chef && ready && received > 0 ? (
        <div
          className="mb-3.5 flex items-center gap-3.5 rounded-[var(--hk-r-lg)] border border-[color:var(--hk-line)] bg-[color:var(--hk-surface)] py-2.5 pl-3 pr-[18px]"
          data-testid="bank-pipeline-veilleur"
        >
          <Veilleur state="done" size="tiny" className="mx-auto" />
          <p className="text-[length:var(--hk-fs-read)]">
            <b>
              {fill(received === 1 ? "bank.pipeline.veilleurUn" : "bank.pipeline.veilleurN", {
                n: String(received),
              })}
            </b>{" "}
            {fill(
              received === 1
                ? noneBlocking
                  ? "bank.pipeline.veilleurAttendUnSans"
                  : "bank.pipeline.veilleurAttendUn"
                : noneBlocking
                  ? "bank.pipeline.veilleurAttendNSans"
                  : "bank.pipeline.veilleurAttendN",
              { n: String(received) },
            )}
          </p>
        </div>
      ) : null}
      <PillTabs
        label={t("bank.pipeline.listeLabel")}
        tabs={tabs}
        showCounts={ready}
        value={chef ? "tous" : tab}
        onChange={setTab}
        testIdOf={(id) => `bank-onglet-${id}`}
      />
      {props.etat === "chargement" ? (
        <SkeletonRows rows={5} />
      ) : props.etat === "erreur" ? (
        <ListState
          kind="error"
          testId="bank-pipeline-erreur"
          title={t("bank.pipeline.erreurTitre")}
          sentence={t("bank.pipeline.erreurPhrase")}
          action={
            <Button variant="secondary" data-testid="bank-pipeline-reessayer" onClick={props.onRetry}>
              <span className="inline-flex items-center gap-2">
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                {t("common.action.reessayer")}
              </span>
            </Button>
          }
        />
      ) : visible.length === 0 ? (
        <ListState
          kind="empty"
          testId="bank-pipeline-vide"
          title={empty ? t("bank.pipeline.videTitre") : t("bank.pipeline.vueVideTitre")}
          sentence={empty ? t("bank.pipeline.videPhrase") : t("bank.pipeline.vueVidePhrase")}
        />
      ) : (
        <ListCard>
          <Table
            columns={[
              {
                key: "dossier",
                header: t("bank.liste.colDossier"),
                cell: (file: FichierBanque) => (
                  <FileWho
                    initials={initialsOf(file.clientName)}
                    name={file.clientName ?? DASH}
                    line={`${file.reference} · ${productOf(file)}`}
                    testId={`ouvrir-${file.reference}`}
                    lineTestId={`fichier-${file.reference}`}
                  />
                ),
              },
              {
                key: "montant",
                header: t("bank.dossiers.colMontant"),
                align: "right",
                cell: (file: FichierBanque) => (
                  <span className="font-[family-name:var(--hk-mono)]" data-testid="bank-montant">
                    {formatMoney(file.amountFinanced).replace(/\s?FCFA$/u, "")}
                  </span>
                ),
              },
              {
                key: "note",
                header: t("bank.dossiers.colNote"),
                cell: (file: FichierBanque) => <GradeChip grade={file.grade} />,
              },
              {
                key: "etape",
                header: t("bank.liste.colEtape"),
                cell: (file: FichierBanque) => (
                  <StageDotChip stage={file.stage} mode={chef ? "all" : "open"} />
                ),
              },
              chef
                ? {
                    key: "analyste",
                    header: t("bank.pipeline.colAnalyste"),
                    cell: (file: FichierBanque) =>
                      props.selfId !== undefined && file.analystUserId === props.selfId ? (
                        <span className="flex flex-col" data-testid="bank-holder-chef">
                          <b className="font-semibold">{t("bank.pipeline.holderVous")}</b>
                          <small
                            className="whitespace-nowrap text-[length:var(--hk-fs-meta)] text-[color:var(--hk-text-2)]"
                            data-meta
                          >
                            {t("bank.pipeline.holderChef")}
                          </small>
                        </span>
                      ) : (
                        <span>
                          {(file.analystUserId === null
                            ? undefined
                            : props.analystNames?.get(file.analystUserId)) ?? DASH}
                        </span>
                      ),
                  }
                : {
                    key: "geste",
                    header: t("bank.pipeline.colGeste"),
                    cell: (file: FichierBanque) => <span>{nextStepOf(file.stage)}</span>,
                  },
              ...(chef
                ? []
                : [
                    {
                      key: "recu",
                      header: t("bank.pipeline.colRecu"),
                      align: "right" as const,
                      cell: (file: FichierBanque) => (
                        <span className="text-[color:var(--hk-text-2)]">
                          {receivedWords(file.receivedDaysAgo ?? null)}
                        </span>
                      ),
                    },
                  ]),
            ]}
            rows={visible}
            rowKey={(file) => file.id}
            rowLink={{
              onOpen: props.onOuvrir,
              label: (file) =>
                fill("bank.liste.ouvrirDossier", { client: file.clientName ?? file.reference }),
            }}
          />
        </ListCard>
      )}
    </div>
  );
}
